import React, { useEffect, useState } from 'react';
import { useCouponsManageContext, CouponWithState } from '../../../../../context/CouponsManageContext';
import CouponDetailsEdit from './CouponDetailsEdit';
import CouponAssessmentEdit from './CouponAssessmentEdit';
import LoadingCoupon from './LoadingCoupon';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CouponCardProps {
  coupon: CouponWithState;
  isDraggable?: boolean;
}

const CouponCard: React.FC<CouponCardProps> = ({ coupon: couponWithState, isDraggable = false }) => {
  const couponId = couponWithState.id;
  const coupon = couponWithState.data;
  const isEditing = true;
  const pendingApproval = couponWithState.pendingApproval;
  const [isLoadingCoupon, setIsLoadingCoupon] = useState(false);

  const {
    isGenerating,
    toggleEdit,
    updateCouponDetails,
    updateAssessment,
    commitCouponHistory,
  } = useCouponsManageContext();

  // Check if this coupon has a pending approval
  const isPendingApproval = !!pendingApproval;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: couponId,
    disabled: isPendingApproval || isGenerating
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    zIndex: isDragging ? 1000 : 10,
  };

  useEffect(() => {
    toggleEdit(couponId);
  }, [couponId]);

  // Use suggestion data if in approval mode, otherwise use regular coupon
  const displayCoupon = isPendingApproval && pendingApproval.suggestion
    ? pendingApproval.suggestion
    : coupon;

  // Show loading skeleton if this coupon is being regenerated
  // Only show loading if this specific coupon is regenerating (isRegenerating), not based on global isGenerating
  const isRegeneratingCoupon = !isEditing && !isPendingApproval || isLoadingCoupon;

  if (isRegeneratingCoupon) {
    return <LoadingCoupon />;
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={isDraggable ? style : {}}
        {...attributes}
        {...listeners}
        className={`flex flex-col gap-2 generate-coupon-card
          ${isEditing || isPendingApproval ? 'editing' : ''}
          ${isDraggable ? 'draggable' : ''}
          `}
      >
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <CouponDetailsEdit
              coupon={displayCoupon}
              onChange={(updates) => {
                updateCouponDetails(couponId, updates);
              }}
              onBlur={() => commitCouponHistory(couponId)}
            />

            <CouponAssessmentEdit
              assessment={coupon?.assessment || null}
              coupon={coupon}
              displayCoupon={displayCoupon}
              pendingApproval={pendingApproval}
              couponId={couponId}
              isLoadingCoupon={setIsLoadingCoupon}
              onChange={(updates) => {
                updateAssessment(couponId, updates);
              }}
              updateContext={(context) => updateCouponDetails(couponId, context)}
              onBlur={() => commitCouponHistory(couponId)}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default CouponCard;

